import type { FunctionComponent } from 'react'
import { ScrollView, View, Text } from 'react-native'
import { Loading } from '@/src/common/components/Loading'
import { StatementsSection } from '@/src/app/main/(tabs)/card/card-management/_common/statementsSection'
import {
  useCardManagementStackScreensAreModals,
  useCardStackScreen,
} from '@/src/app/main/(tabs)/card/card-management/_common/cardStackScreen'
import * as Ids from '@/src/experiences/card-statements/e2e/ids'
import { provideCardPlatform } from '@/src/experiences/card-platform/provideCardPlatform'
import { NoTransactions } from '@/src/experiences/card-activity/icons/no-transactions'
import { mesaColorNames, typography } from '@/src/common/theme'
import { LegacyStatements } from '@/src/experiences/card-statements/Statements/LegacyStatements'
import { PlatformStatements } from '@/src/experiences/card-statements/Statements/PlatformStatements'
import type { Statement as LegacyStatement } from '@api/mesa/graphql'

export type StatementItem = Omit<LegacyStatement, 'isStatementPdfAvailable' | '__typename'>

export function useStatementsScreenProps() {
  const cardManagementStackScreensAreModals = useCardManagementStackScreensAreModals()
  return useCardStackScreen({
    name: 'index',
    options: {
      title: 'Statements',
      ...(cardManagementStackScreensAreModals ? { presentation: 'transparentModal' } : undefined),
      contentStyle: { paddingHorizontal: 16 },
    },
    sectionProps: {
      titleTestID: Ids.Statements,
    },
  })
}

interface StatementsProps {
  statements?: StatementItem[]
  loading?: boolean
  error?: boolean
}

export type StatementsComponent = FunctionComponent<StatementsProps>

export type StatementsProvider = FunctionComponent<{ ToWrap: StatementsComponent }>

export default provideCardPlatform<StatementsComponent>(({ platform, ToWrap }) => {
  switch (platform) {
    case 'HIGHNOTE':
      return <LegacyStatements ToWrap={ToWrap} />
    case 'LOANPRO':
      return <PlatformStatements ToWrap={ToWrap} />
    default:
      platform satisfies undefined
      return <ToWrap loading />
  }
})(function Statements({ statements, loading, error }) {
  const { Modal, routerPush } = useStatementsScreenProps()
  const cardManagementStackScreensAreModals = useCardManagementStackScreensAreModals()

  return Modal({
    children: (
      <ScrollView
        contentContainerStyle={{
          paddingBottom: loading || !cardManagementStackScreensAreModals ? 50 : 0,
        }}
        style={cardManagementStackScreensAreModals && { padding: 24 }}
      >
        {loading ? (
          <Loading />
        ) : error ? (
          <>
            <NoTransactions
              svgProps={{ style: { marginHorizontal: 'auto' } }}
              pathProps={{ fill: mesaColorNames['Mesa - Brand/White #FFFFFF'] }}
            />
            <Text
              style={[
                typography.L,
                {
                  textAlign: 'center',
                  color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
                  marginTop: 32,
                  marginBottom: 32,
                },
              ]}
            >
              Statements unavailable
            </Text>
          </>
        ) : (
          <View
            style={{
              backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'],
              borderRadius: 16,
            }}
          >
            <View
              style={{
                paddingVertical: 16,
                minHeight: 88,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <StatementsSection items={statements} routerPush={routerPush} />
            </View>
          </View>
        )}
      </ScrollView>
    ),
  })
})
